/**
 * PEFSA — Product Quote Drawer v3.2
 * Extiende la clase Drawer nativa de Krank — hereda animación, swipe, drag, overlay, escape.
 * Aquí solo vive la lógica de PEFSA: init del formulario, poblarlo, reset, product key, toggle.
 *
 * v3.2 — BUGFIX CRÍTICO: ENVÍOS DUPLICADOS.
 *
 *   _reset() hacía esto:
 *       delete form.dataset.pefsaInit;          // borra la marca de inicializado
 *       window.PefsaQuoteForm.initForm(form);   // y vuelve a registrar el listener de submit
 *
 *   Y _reset() se llama CADA VEZ que el cliente abre el drawer en un producto distinto.
 *   Resultado: los listeners de 'submit' se ACUMULABAN.
 *
 *       Abre cotizar en producto A  -> listener #2 (el #1 lo puso el init de la página)
 *       Abre cotizar en producto B  -> listener #3
 *       Abre cotizar en producto C y ENVÍA -> se disparan LOS TRES
 *       => 3 quote_id, 3 correos al vendedor, 3 filas en el Sheet. Por UNA cotización.
 *
 *   Ahora _reset() NO borra la marca ni re-registra nada: solo limpia valores y estados.
 *   initForm() es idempotente (sus guards evitan duplicar listeners), así que llamarlo
 *   de nuevo es inofensivo.
 */

class ProductQuoteDrawer extends Drawer {

  connectedCallback() {
    if (typeof super.connectedCallback === 'function') {
      super.connectedCallback();
    }

    this._form              = this.querySelector('#pefsa-drawer-quote-form');
    this._prefix            = 'pefsa-drawer';
    this._currentProductKey = null;

    if (window.PefsaQuoteForm && this._form) {
      window.PefsaQuoteForm.initForm(this._form);
    }
  }

  // ── Abrir — se resetea solo cuando cambia el producto ──────────────────────
  open(data, triggerElement) {
    var nextProductKey =
      String(data.variantId || '') + ':' +
      String(data.productUrl || data.url || '');

    if (this._currentProductKey !== nextProductKey) {
      this._reset();
      this._currentProductKey = nextProductKey;
    }

    this._populate(data);
    this.show(triggerElement);
    this._initDetailsToggle();
  }

  // ── Toggle de detalles — esquiva el click handler nativo del Drawer ──────────
  _initDetailsToggle() {
    var toggle = this.querySelector('#pefsa-drawer-details-toggle');
    var extra  = this.querySelector('#pefsa-drawer-details-extra');

    if (!toggle || !extra || toggle._pefsaToggleInit) return;
    toggle._pefsaToggleInit = true;

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();

      var isOpen = extra.style.display === 'flex';

      extra.style.display       = isOpen ? 'none' : 'flex';
      extra.style.flexDirection = 'column';

      toggle.textContent = isOpen
        ? '+ Datos adicionales (RFC)'
        : '− Datos adicionales (RFC)';

      toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });
  }

  // ── Poblar los campos desde los data-* del disparador ──────────────────────
  _populate(data) {
    var prefix = this._prefix;

    this._setText('pefsa-drawer-product-title', data.productTitle || data.title || '');
    this._setText('pefsa-drawer-sku-display',   data.sku || '');

    var priceEl = document.getElementById('pefsa-drawer-product-price');
    if (priceEl) {
      var cents = parseInt(data.price, 10) || 0;
      priceEl.textContent = '$ ' + (cents / 100).toLocaleString('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }

    var img = document.getElementById('pefsa-drawer-product-image');
    if (img) {
      if (data.productImage || data.image) {
        img.src           = data.productImage || data.image;
        img.alt           = data.productTitle || data.title || '';
        img.style.display = 'block';
      } else {
        img.style.display = 'none';
      }
    }

    var link = document.getElementById('pefsa-drawer-details-link');
    if (link && (data.productUrl || data.url)) {
      link.href = (data.productUrl || data.url) + '#cotizar';
    }

    this._setVal(prefix + '-sku',                     data.sku          || '');
    this._setVal(prefix + '-variante',                data.variantTitle || '');
    this._setVal(prefix + '-variant-id',              data.variantId    || '');
    this._setVal(prefix + '-url',                     data.productUrl || data.url || '');
    this._setVal(prefix + '-disponible-al-solicitar', data.available    || '');

    var cents2 = parseInt(data.price, 10) || 0;
    this._setVal(prefix + '-precio', (cents2 / 100).toFixed(2));

    var form = this._form;
    if (form) {
      this._setFormField(form, 'producto',           data.productTitle || data.title || '');
      this._setFormField(form, 'vendor',             data.productVendor || data.vendor || '');
      this._setFormField(form, 'quote_mode',         data.quoteMode         || '');
      this._setFormField(form, 'catalogo_extendido', data.catalogoExtendido || '');
      this._setFormField(form, 'origen_formulario',  'plp_drawer');
    }

    var liEl = document.getElementById(prefix + '-line-items');
    if (liEl) {
      liEl.value = JSON.stringify([{
        sku:        data.sku          || '',
        variant_id: data.variantId    || '',
        title:      data.productTitle || data.title || '',
        vendor:     data.productVendor || data.vendor || '',
        quantity:   1,
        url:        data.productUrl || data.url || ''
      }]);
    }

    var uaEl = document.getElementById(prefix + '-ua');
    if (uaEl) uaEl.value = navigator.userAgent;

    if (window.PefsaQuoteForm) {
      window.PefsaQuoteForm.injectUTMs(this._form);
    }
  }

  // ── Reset — limpia valores y estados, SIN tocar los listeners ───────────────
  _reset() {
    var form      = this._form;
    var prefix    = this._prefix;
    var successEl = document.getElementById(prefix + '-quote-success');
    var errorEl   = document.getElementById(prefix + '-quote-error');
    var btn       = document.getElementById(prefix + '-quote-btn');
    var extraEl   = document.getElementById(prefix + '-details-extra');
    var toggleEl  = document.getElementById(prefix + '-details-toggle');

    if (form) {
      form.reset();
      form.style.display = '';
      // v3.2 — OJO: NO se borra form.dataset.pefsaInit.
      // Borrarlo hacía que initForm() volviera a registrar el listener de 'submit',
      // y como _reset() corre cada vez que cambia el producto, los listeners se
      // acumulaban => la misma cotización se enviaba N veces.
    }

    if (successEl) successEl.style.display = 'none';
    if (errorEl)   errorEl.style.display   = 'none';

    if (btn) {
      btn.disabled    = false;
      btn.textContent = 'Solicitar cotización';
    }

    if (extraEl) extraEl.style.display = 'none';

    if (toggleEl) {
      toggleEl.textContent = '+ Datos adicionales (RFC)';
      toggleEl.setAttribute('aria-expanded', 'false');
      // v3.2 — tampoco se resetea _pefsaToggleInit: el listener del toggle ya está
      // puesto y debe quedarse puesto UNA sola vez.
    }

    // initForm es idempotente: sus guards evitan duplicar listeners.
    if (window.PefsaQuoteForm && form) {
      window.PefsaQuoteForm.initForm(form);
    }
  }

  // ── Helpers ───────────────────────────────────────────────────
  _setText(id, value) {
    var el = document.getElementById(id);
    if (el) el.textContent = value || '';
  }

  _setVal(id, value) {
    var el = document.getElementById(id);
    if (el) el.value = value || '';
  }

  _setFormField(form, name, value) {
    var el = form.querySelector('[name="' + name + '"]');
    if (el) el.value = value || '';
  }
}

// ── Registrar el custom element ──────────────────────────────────────
if (!customElements.get('product-quote-drawer')) {
  customElements.define('product-quote-drawer', ProductQuoteDrawer);
}

// ── Disparador global ────────────────────────────────────────────
document.addEventListener('click', function (e) {
  var trigger = e.target.closest('[data-quote-drawer]');
  if (!trigger) return;

  e.preventDefault();
  e.stopPropagation();

  var productUrl = trigger.dataset.productUrl || '';
  var drawer     = document.getElementById('Product-Quote-Drawer');

  if (!drawer || typeof drawer.open !== 'function') {
    if (productUrl) window.location.href = productUrl + '#cotizar';
    return;
  }

  drawer.open(trigger.dataset, trigger);
});
